import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Textarea,
} from '@roger/ui-components';

import { bulkAppendTaskNotes } from '../../services/api';
import type { TaskListResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { lockedNoteSkipNotice, partitionNoteable } from './batchActions';

interface BatchNotesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The whole selection, not just the eligible rows: the dialog partitions it
   * itself so it can disclose what it is about to skip.
   */
  tasks: TaskListResponse[];
  /** Called after a successful append, with the number of tasks written. */
  onSuccess: (count: number) => void;
}

const ERROR_TITLE = "Couldn't add note";

/**
 * Appends one sticky note to every eligible task in a batch selection.
 *
 * The skip disclosure lives here rather than in the action bar's own confirm
 * step, so the action opens exactly one dialog. Routing it through the confirm
 * step would close that dialog and open this one in the same commit, which is
 * the body pointer-events hazard the bar's dropdown handoff already defers
 * around, and it would cost the user a second click to reach the text box.
 */
const BatchNotesDialog: React.FC<BatchNotesDialogProps> = ({
  open,
  onOpenChange,
  tasks,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { error: showError } = useToast();

  const { noteable, skippedLockedMisc } = useMemo(
    () => partitionNoteable(tasks),
    [tasks]
  );
  const skipNotice = lockedNoteSkipNotice(skippedLockedMisc.length);
  const hasEligible = noteable.length > 0;

  useEffect(() => {
    if (open) setNotes('');
  }, [open]);

  const reportFailure = (failure: unknown) => {
    console.error('Batch add note failed:', failure);
    const status = getHttpStatus(failure);
    if (status === 404) {
      showError(
        ERROR_TITLE,
        'One or more selected tasks could not be found. Refresh and try again.'
      );
      return;
    }
    if (status === 409) {
      showError(
        ERROR_TITLE,
        'Some selected tasks are signed and can no longer be edited.'
      );
      return;
    }
    showError(ERROR_TITLE, 'Something went wrong. Please try again.');
  };

  const handleSubmit = async () => {
    const trimmed = notes.trim();
    if (!trimmed || !hasEligible || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await bulkAppendTaskNotes({
        body: { task_ids: noteable.map(task => task.id), notes: trimmed },
      });
      if (result.error) {
        // The client resolves non-2xx rather than throwing, so the error branch
        // has to be read off the result as well as caught below.
        reportFailure(result);
        return;
      }
      onOpenChange(false);
      // The server's count, not the local one: the two agree while the endpoint
      // is all-or-nothing, but its response carries a `failed` list, so a later
      // move to partial success would leave the local count overstating it.
      onSuccess(result.data?.updated ?? noteable.length);
    } catch (err) {
      reportFailure(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && isSubmitting) return;
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {hasEligible
              ? `Add note to ${noteable.length} task${
                  noteable.length === 1 ? '' : 's'
                }`
              : 'Nothing to annotate'}
          </DialogTitle>
        </DialogHeader>
        {hasEligible ? (
          <>
            {/* Always rendered, not only when something is skipped: a
              DialogContent with no description is announced without one. The
              skip line replaces the general one rather than joining it, which
              would claim every selected task and then take one back. */}
            <DialogDescription>
              {skipNotice ?? 'The note is added to every selected task.'}
            </DialogDescription>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Enter a note...'
              rows={4}
              aria-label='Note'
            />
          </>
        ) : (
          <DialogDescription>
            The selected tasks are signed, so their notes can no longer be
            edited.
          </DialogDescription>
        )}
        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {hasEligible ? 'Cancel' : 'Close'}
          </Button>
          {hasEligible && (
            <Button
              onClick={() => void handleSubmit()}
              disabled={isSubmitting || !notes.trim()}
            >
              {isSubmitting && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              Add note
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BatchNotesDialog;
